import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";
import { PlaceholderExtension } from "@remirror/extension-placeholder";
import { LinkExtension } from "@remirror/extension-link";
import { FontSizeExtension } from "@remirror/extension-font-size";
import { HeadingExtension } from "@remirror/extension-heading";
import { FontFamilyExtension } from "@remirror/extension-font-family";
import { TextColorExtension } from "@remirror/extension-text-color";
import { EmojiExtension } from "@remirror/extension-emoji";
import { wysiwygPreset } from "@remirror/preset-wysiwyg";
import {
  EditorComponent,
  Remirror,
  ThemeProvider,
  useRemirror,
  useEditorEvent,
  useHelpers,
  EmojiPopupComponent,
} from "@remirror/react";
import { AllStyledComponent } from "@remirror/styles/emotion";
import data from "svgmoji/emoji.json";

import {
  FrameworkProps,
  AnyExtension,
  CreateEditorStateProps,
  EditorView,
  RemirrorJSON,
  PlainExtension,
  InvalidContentHandler,
} from "@remirror/core";
import { WysiwygToolbar } from "../WysiwygToolbar";
import isEqual from "lodash.isequal";

import { ColorOrTransparent } from "../../types";
import { useLinkState } from "../../hooks/useLinkState";
import { CustomImageExtension } from "../../extensions";
import { BackgroundColorProps } from "../WysiwygToolbar/Buttons";
import { FloatingLinkToolbar } from "../FloatingLinkToolbar";
import "./RichTextEditor.css";

type Label = RemirrorJSON | string | undefined;

export interface RichTextEditorHandle {
  focus: () => void;
}

interface ReactEditorProps<T extends AnyExtension>
  extends Pick<CreateEditorStateProps, "stringHandler">,
    Pick<FrameworkProps<T>, "editable"> {
  canvasId: string;
  nodeId: string;
  placeholder: string;
  initialContent: Label;
  onBlur: (json: RemirrorJSON) => void;
  onBackgroundColorChange: BackgroundColorProps["onBackgroundColorChange"];
  backgroundColor: ColorOrTransparent;
  isToolbarVisible: boolean;
  onImageUploadError: () => void;
}

export interface RichTextEditorProps<T extends AnyExtension>
  extends ReactEditorProps<T> {}

const customTheme = {
  fontFamily: {
    default: "Inter, sans-serif",
  },
  radius: {
    border: "3px",
  },
  space: {
    3: 0,
  },
};

class PasteHandlerExtension extends PlainExtension {
  get name() {
    return "pasteHandler" as const;
  }

  createPlugin() {
    return {
      props: {
        handlePaste: (view: EditorView, event: ClipboardEvent) => {
          if (event.clipboardData) {
            const text = event.clipboardData.getData("text");
            const { state, dispatch } = view;

            const isURL = /^(https?:\/\/[^\s]+)/.test(text);

            const { from, to } = state.selection;
            const hasTextSelection = from !== to;

            if (dispatch && isURL && hasTextSelection) {
              const linkMark = state.schema.marks.link.create({ href: text });
              const linkText = state.doc.textBetween(from, to);
              const textNode = state.schema.text(linkText, [linkMark]);

              // Replace the selected text with the link mark
              dispatch(state.tr.replaceWith(from, to, textNode));
              return true;
            }

            return false; // Let ProseMirror handle the event if clipboardData is not available
          }

          return false;
        },
      },
    };
  }
}

export const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  RichTextEditorProps<AnyExtension>
>(function RichTextEditor<T extends AnyExtension>(
  {
    canvasId,
    nodeId,
    placeholder,
    stringHandler,
    initialContent,
    onBlur,
    isToolbarVisible,
    onBackgroundColorChange,
    backgroundColor,
    editable,
    onImageUploadError,
    ...rest
  }: RichTextEditorProps<T>,
  ref: Ref<RichTextEditorHandle>,
) {
  const extensions = useCallback(() => {
    const linkExtension = new LinkExtension({
      autoLink: true,
      defaultTarget: "_blank",
      selectTextOnClick: true,
    });

    if (!editable) {
      linkExtension.addHandler("onClick", (_, data) => {
        const newTab = window.open(data.href, "_blank");
        newTab?.focus();
        return true;
      });
    }

    return [
      new PlaceholderExtension({ placeholder }),
      new FontSizeExtension({ defaultSize: "16", unit: "px" }),
      new HeadingExtension({}),
      new TextColorExtension({}),
      new FontFamilyExtension(),
      new EmojiExtension({ data, moji: "noto" }),
      linkExtension,
      new PasteHandlerExtension(),
      new CustomImageExtension({
        canvasId,
        onImageUploadError,
        enableResizing: true,
      }),
      ...wysiwygPreset(), // This must go last to ensure our custom extensions are not overwritten by the preset
    ];
  }, [editable, placeholder, canvasId, onImageUploadError]);

  const localRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (localRef.current) {
        const contentEditableDiv = localRef.current.querySelector(
          'div[contenteditable="true"][role="textbox"]',
        ) as HTMLDivElement;

        // Only focus if contentEditableDiv is not already focused
        if (
          contentEditableDiv &&
          document.activeElement !== contentEditableDiv
        ) {
          contentEditableDiv.focus();
        }
      }
    },
  }));

  const onError: InvalidContentHandler = useCallback(
    ({ json, invalidContent, transformers }) => {
      // Automatically remove all invalid nodes and marks.
      return transformers.remove(json, invalidContent);
    },
    [],
  );

  const { manager, state } = useRemirror({
    extensions,
    stringHandler: "html",
    content: initialContent,
    onError,
  });

  const baseClasses = ["rich-text-editor", "nodrag"];
  const classNames = editable ? baseClasses : [...baseClasses, "view-only"];

  return (
    <div className="rich-text-editor-wrapper" ref={localRef}>
      <AllStyledComponent>
        <ThemeProvider theme={customTheme}>
          <Remirror
            manager={manager}
            initialContent={state}
            editable={editable}
            classNames={classNames}
            {...rest}
          >
            {isToolbarVisible && (
              <>
                <WysiwygToolbar
                  nodeId={nodeId}
                  backgroundColor={backgroundColor}
                  onBackgroundColorChange={onBackgroundColorChange}
                  onBlur={onBlur}
                />
                <FloatingLinkToolbar nodeId={nodeId} />
              </>
            )}
            <Editor
              nodeId={nodeId}
              onBlur={onBlur}
              initialContent={initialContent}
            />
            <EmojiPopupComponent />
          </Remirror>
        </ThemeProvider>
      </AllStyledComponent>
    </div>
  );
});

function Editor({
  onBlur,
  initialContent,
  nodeId,
}: {
  onBlur: (json: RemirrorJSON) => void;
  initialContent: Label;
  nodeId: string;
}) {
  const { getJSON } = useHelpers();
  const previousJSON = useRef<Label>(initialContent);
  const { isEditing, cancelHref } = useLinkState(nodeId);

  const closeLinkDialog = useCallback(() => {
    if (!isEditing) {
      return;
    }

    cancelHref();
  }, [isEditing, cancelHref]);

  const handleOnBlur = useCallback(() => {
    closeLinkDialog();

    const currentJSON = getJSON();
    const hasChanges = !isEqual(previousJSON.current, currentJSON);

    if (hasChanges) {
      onBlur(currentJSON);
      previousJSON.current = currentJSON;
    }
  }, [getJSON, onBlur, closeLinkDialog]);

  useEditorEvent("blur", handleOnBlur);

  return <EditorComponent />;
}
